import { useInputControl } from '@conform-to/react'
import { REGEXP_ONLY_DIGITS_AND_CHARS, type OTPInputProps } from 'input-otp'
import React, { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '#app/utils/misc.tsx'
import { Checkbox, type CheckboxProps } from './ui/checkbox.tsx'
import {
	InputOTP,
	InputOTPGroup,
	InputOTPSeparator,
	InputOTPSlot,
} from './ui/input-otp.tsx'
import { Input } from './ui/input.tsx'
import { Label } from './ui/label.tsx'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from './ui/select.tsx'
import { Textarea } from './ui/textarea.tsx'

export type ListOfErrors = Array<string | null | undefined> | null | undefined

export function ErrorList({
	id,
	errors,
}: {
	errors?: ListOfErrors
	id?: string
}) {
	const errorsToRender = errors?.filter(Boolean)
	if (!errorsToRender?.length) return null
	return (
		<ul id={id} className="flex flex-col gap-1">
			{errorsToRender.map((e) => (
				<li key={e} className="text-destructive text-[0.8rem] font-medium">
					{e}
				</li>
			))}
		</ul>
	)
}

export function Field({
	labelProps,
	inputProps,
	errors,
	className,
}: {
	labelProps: React.LabelHTMLAttributes<HTMLLabelElement>
	inputProps: React.InputHTMLAttributes<HTMLInputElement>
	errors?: ListOfErrors
	className?: string
}) {
	const fallbackId = useId()
	const id = inputProps.id ?? fallbackId
	const errorId = errors?.length ? `${id}-error` : undefined
	return (
		<div className={cn('grid gap-3', className)}>
			<Label htmlFor={id} {...labelProps} />
			<Input
				id={id}
				aria-invalid={errorId ? true : undefined}
				aria-describedby={errorId}
				{...inputProps}
			/>
			{errorId ? <ErrorList id={errorId} errors={errors} /> : null}
		</div>
	)
}

export function OTPField({
	labelProps,
	inputProps,
	errors,
	className,
}: {
	labelProps: React.LabelHTMLAttributes<HTMLLabelElement>
	inputProps: Partial<OTPInputProps & { render: never }>
	errors?: ListOfErrors
	className?: string
}) {
	const fallbackId = useId()
	const id = inputProps.id ?? fallbackId
	const errorId = errors?.length ? `${id}-error` : undefined
	return (
		<div className={cn('grid gap-3', className)}>
			<Label htmlFor={id} {...labelProps} />
			<InputOTP
				pattern={REGEXP_ONLY_DIGITS_AND_CHARS}
				maxLength={6}
				id={id}
				aria-invalid={errorId ? true : undefined}
				aria-describedby={errorId}
				{...inputProps}
			>
				<InputOTPGroup>
					<InputOTPSlot index={0} />
					<InputOTPSlot index={1} />
					<InputOTPSlot index={2} />
				</InputOTPGroup>
				<InputOTPSeparator />
				<InputOTPGroup>
					<InputOTPSlot index={3} />
					<InputOTPSlot index={4} />
					<InputOTPSlot index={5} />
				</InputOTPGroup>
			</InputOTP>
			{errorId ? <ErrorList id={errorId} errors={errors} /> : null}
		</div>
	)
}

export function TextareaField({
	labelProps,
	textareaProps,
	errors,
	className,
}: {
	labelProps: React.LabelHTMLAttributes<HTMLLabelElement>
	textareaProps: React.TextareaHTMLAttributes<HTMLTextAreaElement>
	errors?: ListOfErrors
	className?: string
}) {
	const fallbackId = useId()
	const id = textareaProps.id ?? textareaProps.name ?? fallbackId
	const errorId = errors?.length ? `${id}-error` : undefined
	return (
		<div className={cn('grid gap-3', className)}>
			<Label htmlFor={id} {...labelProps} />
			<Textarea
				id={id}
				aria-invalid={errorId ? true : undefined}
				aria-describedby={errorId}
				{...textareaProps}
			/>
			{errorId ? <ErrorList id={errorId} errors={errors} /> : null}
		</div>
	)
}

export function CheckboxField({
	labelProps,
	buttonProps,
	errors,
	className,
}: {
	labelProps: React.ComponentProps<'label'>
	buttonProps: CheckboxProps & {
		name: string
		form: string
		value?: string
	}
	errors?: ListOfErrors
	className?: string
}) {
	const { key, defaultChecked, ...checkboxProps } = buttonProps
	const fallbackId = useId()
	const checkedValue = buttonProps.value ?? 'on'
	const input = useInputControl({
		key,
		name: buttonProps.name,
		formId: buttonProps.form,
		initialValue: defaultChecked ? checkedValue : undefined,
	})
	const id = buttonProps.id ?? fallbackId
	const errorId = errors?.length ? `${id}-error` : undefined

	return (
		<div className={cn('grid gap-3', className)}>
			<div className="flex gap-2">
				<Checkbox
					{...checkboxProps}
					id={id}
					aria-invalid={errorId ? true : undefined}
					aria-describedby={errorId}
					checked={input.value === checkedValue}
					onCheckedChange={(state) => {
						input.change(state.valueOf() ? checkedValue : '')
						buttonProps.onCheckedChange?.(state)
					}}
					onFocus={(event) => {
						input.focus()
						buttonProps.onFocus?.(event)
					}}
					onBlur={(event) => {
						input.blur()
						buttonProps.onBlur?.(event)
					}}
					type="button"
				/>
				<label
					htmlFor={id}
					{...labelProps}
					className="text-body-xs text-muted-foreground self-center"
				/>
			</div>
			<div className="px-4 pt-1 pb-3">
				{errorId ? <ErrorList id={errorId} errors={errors} /> : null}
			</div>
		</div>
	)
}

export function SelectField({
	labelProps,
	inputProps,
	errors,
	className,
	options,
	placeholder,
}: {
	labelProps: React.LabelHTMLAttributes<HTMLLabelElement>
	inputProps: React.InputHTMLAttributes<HTMLInputElement>
	errors?: ListOfErrors
	className?: string
	options: Array<{ value: string; label: string }>
	placeholder?: string
}) {
	const fallbackId = useId()
	const id = inputProps.id ?? inputProps.name ?? fallbackId
	const errorId = errors?.length ? `${id}-error` : undefined
	const [value, setValue] = useState(inputProps.defaultValue?.toString() || '')

	return (
		<div className={cn('grid gap-3', className)}>
			<Label htmlFor={id} {...labelProps} />
			<input type="hidden" name={inputProps.name} value={value} />
			<Select value={value} onValueChange={setValue}>
				<SelectTrigger
					id={id}
					aria-invalid={errorId ? true : undefined}
					aria-describedby={errorId}
				>
					<SelectValue placeholder={placeholder} />
				</SelectTrigger>
				<SelectContent>
					{options.map((option) => (
						<SelectItem key={option.value} value={option.value}>
							{option.label}
						</SelectItem>
					))}
				</SelectContent>
			</Select>
			{errorId ? <ErrorList id={errorId} errors={errors} /> : null}
		</div>
	)
}

/**
 * Controlled Combobox component with search functionality
 */
export function Combobox({
	value,
	onValueChange,
	options,
	placeholder,
	emptyMessage,
	className,
}: {
	value: string
	onValueChange: (value: string) => void
	options: Array<{ value: string; label: string }>
	placeholder?: string
	emptyMessage?: string
	className?: string
}) {
	const { t } = useTranslation()
	const [open, setOpen] = React.useState(false)
	const [search, setSearch] = React.useState('')
	const containerRef = React.useRef<HTMLDivElement>(null)

	const filteredOptions = React.useMemo(() => {
		if (!search) return options
		return options.filter((option) =>
			option.label.toLowerCase().includes(search.toLowerCase()),
		)
	}, [options, search])

	const selectedOption = options.find((option) => option.value === value)
	const searchPlaceholder = placeholder || t('search.placeholder')
	const noResultsMessage = emptyMessage || t('search.noResultsFound')

	// Close dropdown when clicking outside
	React.useEffect(() => {
		function handleClickOutside(event: MouseEvent) {
			if (
				containerRef.current &&
				!containerRef.current.contains(event.target as Node)
			) {
				setOpen(false)
				setSearch('')
			}
		}
		document.addEventListener('mousedown', handleClickOutside)
		return () => document.removeEventListener('mousedown', handleClickOutside)
	}, [])

	return (
		<div ref={containerRef} className={cn('relative', className)}>
			<button
				type="button"
				className="border-input bg-background ring-offset-background placeholder:text-muted-foreground focus-visible:ring-ring flex h-9 w-full items-center justify-between rounded-md border px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-hidden disabled:cursor-not-allowed disabled:opacity-50"
				onClick={() => setOpen(!open)}
				aria-expanded={open}
				aria-haspopup="listbox"
			>
				<span
					className={cn(
						'truncate',
						selectedOption ? '' : 'text-muted-foreground',
					)}
				>
					{selectedOption ? selectedOption.label : searchPlaceholder}
				</span>
				<svg
					className={cn(
						'ml-2 h-4 w-4 shrink-0 opacity-50 transition-transform',
						open && 'rotate-180',
					)}
					fill="none"
					stroke="currentColor"
					viewBox="0 0 24 24"
				>
					<path
						strokeLinecap="round"
						strokeLinejoin="round"
						strokeWidth={2}
						d="M19 9l-7 7-7-7"
					/>
				</svg>
			</button>

			{open && (
				<div className="bg-popover absolute top-full z-50 mt-1 w-full min-w-[200px] rounded-md border p-1 shadow-lg">
					<Input
						placeholder={t('search.placeholder')}
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						className="mb-2 h-8"
						autoFocus
					/>
					<div className="max-h-60 overflow-y-auto">
						{filteredOptions.length === 0 ? (
							<div className="text-muted-foreground px-3 py-2 text-sm">
								{noResultsMessage}
							</div>
						) : (
							filteredOptions.map((option) => (
								<button
									key={option.value}
									type="button"
									className={cn(
										'hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground w-full rounded-sm px-3 py-2 text-left text-sm focus:outline-none',
										option.value === value && 'bg-accent',
									)}
									onClick={() => {
										onValueChange(option.value)
										setOpen(false)
										setSearch('')
									}}
								>
									{option.label}
								</button>
							))
						)}
					</div>
				</div>
			)}
		</div>
	)
}

export function ComboboxField({
	labelProps,
	inputProps,
	errors,
	className,
	options,
	placeholder,
	emptyMessage,
}: {
	labelProps: React.LabelHTMLAttributes<HTMLLabelElement>
	inputProps: React.InputHTMLAttributes<HTMLInputElement>
	errors?: ListOfErrors
	className?: string
	options: Array<{ value: string; label: string }>
	placeholder?: string
	emptyMessage?: string
}) {
	const { t } = useTranslation()
	const fallbackId = useId()
	const id = inputProps.id ?? inputProps.name ?? fallbackId
	const errorId = errors?.length ? `${id}-error` : undefined

	const [open, setOpen] = React.useState(false)
	const [search, setSearch] = React.useState('')
	const [selectedValue, setSelectedValue] = React.useState(
		inputProps.defaultValue?.toString() || '',
	)

	const filteredOptions = React.useMemo(() => {
		if (!search) return options
		return options.filter((option) =>
			option.label.toLowerCase().includes(search.toLowerCase()),
		)
	}, [options, search])

	const selectedOption = options.find(
		(option) => option.value === selectedValue,
	)

	// Use provided placeholders or fallback to translations
	const searchPlaceholder = placeholder || t('search.searchExercises')
	const noResultsMessage = emptyMessage || t('search.noResultsFound')

	return (
		<div className={cn('grid gap-3', className)}>
			<Label htmlFor={id} {...labelProps} />
			<div className="relative">
				<button
					type="button"
					className="border-input bg-background ring-offset-background placeholder:text-muted-foreground focus-visible:ring-ring aria-[invalid]:border-input-invalid flex h-10 w-full items-center justify-between rounded-md border px-3 py-2 text-base focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-hidden disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
					onClick={() => setOpen(!open)}
					aria-expanded={open}
					aria-haspopup="listbox"
				>
					<span className={selectedOption ? '' : 'text-muted-foreground'}>
						{selectedOption ? selectedOption.label : searchPlaceholder}
					</span>
					<svg
						className={cn(
							'h-4 w-4 opacity-50 transition-transform',
							open && 'rotate-180',
						)}
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
					>
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth={2}
							d="M19 9l-7 7-7-7"
						/>
					</svg>
				</button>

				{open && (
					<div className="bg-popover absolute top-full z-50 mt-1 w-full rounded-md border p-1 shadow-lg">
						<Input
							placeholder={t('search.searchExercises')}
							value={search}
							onChange={(e) => setSearch(e.target.value)}
							className="mb-2"
						/>
						<div className="max-h-60 overflow-y-auto">
							{filteredOptions.length === 0 ? (
								<div className="text-muted-foreground px-3 py-2 text-sm">
									{noResultsMessage}
								</div>
							) : (
								filteredOptions.map((option) => (
									<button
										key={option.value}
										type="button"
										className="hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground w-full rounded-sm px-3 py-2 text-left text-sm focus:outline-none"
										onClick={() => {
											setSelectedValue(option.value)
											setOpen(false)
											setSearch('')
										}}
									>
										{option.label}
									</button>
								))
							)}
						</div>
					</div>
				)}
			</div>

			{/* Hidden input for form submission */}
			<input
				{...inputProps}
				type="hidden"
				value={selectedValue}
				id={id}
				aria-invalid={errorId ? true : undefined}
				aria-describedby={errorId}
			/>

			{errorId ? <ErrorList id={errorId} errors={errors} /> : null}
		</div>
	)
}

export function FileField({
	labelProps,
	inputProps,
	errors,
	className,
}: {
	labelProps: React.LabelHTMLAttributes<HTMLLabelElement>
	inputProps: React.InputHTMLAttributes<HTMLInputElement>
	errors?: ListOfErrors
	className?: string
}) {
	const fallbackId = useId()
	const id = inputProps.id ?? fallbackId
	const errorId = errors?.length ? `${id}-error` : undefined
	return (
		<div className={cn('grid gap-3', className)}>
			<Label htmlFor={id} {...labelProps} />
			<Input
				id={id}
				type="file"
				aria-invalid={errorId ? true : undefined}
				aria-describedby={errorId}
				className="file:bg-primary file:text-primary-foreground hover:file:bg-primary/90 cursor-pointer file:mr-4 file:h-full file:rounded-md file:border-0 file:px-3 file:text-sm file:font-medium"
				{...inputProps}
			/>
			{errorId ? <ErrorList id={errorId} errors={errors} /> : null}
		</div>
	)
}
